import { isCompedEntitlement, LSC_BILLING_OWNER_MANAGED_COPY } from './billingCopy';
import { isActivePaidAccountStatus } from './entitlementBillingErrors';
import { NAV_LABEL_IMPORT_SYNC } from './leagueNav';
import { lscPath } from './routing';
import { mapReadinessToCommandCenterChecklist } from './leagueReadinessMapper';
import type {
  LscAutomationCoverage,
  LscBillingStatusResponse,
  LscCommissionerDigest,
  LscLeagueReadiness,
  LscLeagueReadinessOverallStatus,
  LscLiveReadinessReport,
  LscVodSyncReadiness,
} from './types';

export type ReadinessItemStatus = 'complete' | 'warning' | 'blocked' | 'not_applicable';

export interface ReadinessChecklistItem {
  id: string;
  label: string;
  status: ReadinessItemStatus;
  explanation: string;
  actionLabel: string;
  actionHref: string;
  isActionable?: boolean;
}

export type NextActionSeverity = 'blocked' | 'warning' | 'info' | 'success';

export interface PaidBetaNextAction {
  priority: number;
  title: string;
  detail: string;
  actionLabel: string;
  actionHref: string;
  severity: NextActionSeverity;
}

export interface PaidBetaReadinessInput {
  leagueId: string;
  billingStatus?: LscBillingStatusResponse | null;
  isOwner?: boolean;
  memberCount?: number;
  matchCount?: number;
  archiveCount?: number;
  candidateCount?: number;
  vodSyncReadiness?: LscVodSyncReadiness | null;
  liveReadiness?: LscLiveReadinessReport | null;
  digest?: LscCommissionerDigest | null;
}

export interface PaidBetaReadinessAssessment {
  items: ReadinessChecklistItem[];
  nextAction: PaidBetaNextAction | null;
  setupComplete: boolean;
  setupIncomplete: boolean;
  completedCount: number;
  totalCount?: number;
  shouldShowChecklist?: boolean;
  overallStatus?: LscLeagueReadinessOverallStatus;
}

const AUTOMATION_NOT_ENABLED_COPY =
  'Automation coverage is not enabled for this league yet.';
const AUTOMATION_ENABLED_COPY = 'Automation coverage is enabled for this league.';
const LEGACY_ROLLOUT_AUTOMATION_COPY =
  'Enabled through complimentary rollout access.';
const VOD_PREP_PREVIEW_COPY =
  'Stream-to-archive prep is running in preview mode until validation completes.';
const BIND_SUBSCRIPTION_AUTOMATION_COPY =
  'Link an active subscription before automation can be enabled.';
const ARCHIVE_COMMISSIONER_COPY = 'Publishing remains commissioner-controlled.';

function getAutomationCoverage(digest?: LscCommissionerDigest | null): LscAutomationCoverage | null {
  return digest?.automation?.automation_coverage ?? null;
}

function isAutomationCoverageEligible(digest?: LscCommissionerDigest | null): boolean {
  const coverage = getAutomationCoverage(digest);
  if (coverage) {
    return coverage.eligible === true;
  }
  return digest?.automation?.pilot_league_allowed === true;
}

function automationCoverageMode(
  digest?: LscCommissionerDigest | null
): 'legacy_pilot' | 'paid_beta_entitlement' | 'blocked' | undefined {
  return getAutomationCoverage(digest)?.mode;
}

function automationNotEnabledCopy(
  billingStatus?: LscBillingStatusResponse | null,
  digest?: LscCommissionerDigest | null
): string {
  if (isSubscriptionUnbound(billingStatus)) {
    return BIND_SUBSCRIPTION_AUTOMATION_COPY;
  }
  const mode = automationCoverageMode(digest);
  if (mode === 'legacy_pilot') {
    return LEGACY_ROLLOUT_AUTOMATION_COPY;
  }
  return AUTOMATION_NOT_ENABLED_COPY;
}

function automationEnabledCopy(digest?: LscCommissionerDigest | null): string {
  const mode = automationCoverageMode(digest);
  if (mode === 'legacy_pilot') {
    return LEGACY_ROLLOUT_AUTOMATION_COPY;
  }
  return AUTOMATION_ENABLED_COPY;
}

function leagueBase(leagueId: string): string {
  return lscPath(`/leagues/${leagueId}`);
}

function hasActiveLeagueEntitlement(billingStatus?: LscBillingStatusResponse | null): boolean {
  const entitlement = billingStatus?.entitlement;
  if (!entitlement) return false;
  if (isCompedEntitlement(entitlement)) return true;
  const status = (entitlement.subscription_status || billingStatus?.subscription_status || '')
    .toLowerCase()
    .trim();
  return status === 'active' || status === 'trialing' || status === 'comped';
}

function isBillingMissing(billingStatus?: LscBillingStatusResponse | null): boolean {
  if (!billingStatus?.billing_model_enabled) return false;
  if (hasActiveLeagueEntitlement(billingStatus)) return false;
  return !isActivePaidAccountStatus(billingStatus);
}

function isSubscriptionUnbound(billingStatus?: LscBillingStatusResponse | null): boolean {
  if (!billingStatus?.billing_model_enabled) return false;
  if (billingStatus.entitlement) return false;
  return isActivePaidAccountStatus(billingStatus);
}

function buildBillingItem(
  leagueId: string,
  billingStatus?: LscBillingStatusResponse | null,
  isOwner?: boolean
): ReadinessChecklistItem {
  const settingsHref = `${leagueBase(leagueId)}/settings`;
  const billingHref = lscPath('/billing');
  const pricingHref = lscPath('/pricing');

  if (isOwner === false) {
    return {
      id: 'billing',
      label: 'League subscription',
      status: 'not_applicable',
      explanation: LSC_BILLING_OWNER_MANAGED_COPY,
      actionLabel: 'League settings',
      actionHref: settingsHref,
    };
  }

  if (!billingStatus?.billing_model_enabled) {
    return {
      id: 'billing',
      label: 'League subscription',
      status: 'not_applicable',
      explanation: 'Subscription billing is not enabled on this environment yet.',
      actionLabel: 'View billing',
      actionHref: billingHref,
    };
  }

  const entitlement = billingStatus.entitlement;
  const subStatus = (
    entitlement?.subscription_status ||
    billingStatus.subscription_status ||
    ''
  )
    .toLowerCase()
    .trim();

  if (entitlement && isCompedEntitlement(entitlement)) {
    return {
      id: 'billing',
      label: 'League subscription',
      status: 'complete',
      explanation: 'Complimentary access is active for this league.',
      actionLabel: 'League settings',
      actionHref: settingsHref,
    };
  }

  if (subStatus === 'past_due') {
    return {
      id: 'billing',
      label: 'League subscription',
      status: 'warning',
      explanation: 'Subscription payment needs attention before changes can continue.',
      actionLabel: 'Manage billing',
      actionHref: billingHref,
    };
  }

  if (subStatus === 'canceled' || subStatus === 'cancelled') {
    return {
      id: 'billing',
      label: 'League subscription',
      status: 'blocked',
      explanation: 'Subscription is canceled. Renew to restore league access.',
      actionLabel: 'Activate subscription',
      actionHref: pricingHref,
    };
  }

  if (subStatus === 'suspended') {
    return {
      id: 'billing',
      label: 'League subscription',
      status: 'blocked',
      explanation: 'League Stream Center access is suspended. Contact support for help.',
      actionLabel: 'Manage billing',
      actionHref: billingHref,
    };
  }

  if (entitlement && hasActiveLeagueEntitlement(billingStatus)) {
    const cadence = entitlement.billing_cadence || billingStatus.billing_cadence;
    return {
      id: 'billing',
      label: 'League subscription',
      status: 'complete',
      explanation: `Subscription is active for this league${cadence ? ` (${cadence})` : ''}.`,
      actionLabel: 'Manage billing',
      actionHref: billingHref,
    };
  }

  if (isSubscriptionUnbound(billingStatus)) {
    return {
      id: 'billing',
      label: 'League subscription',
      status: 'blocked',
      explanation:
        'Your subscription is active, but it is not linked to this league yet.',
      actionLabel: 'Link subscription',
      actionHref: settingsHref,
    };
  }

  return {
    id: 'billing',
    label: 'League subscription',
    status: 'blocked',
    explanation: 'This league does not have an active subscription yet.',
    actionLabel: 'Activate subscription',
    actionHref: settingsHref,
  };
}

function buildMembersItem(leagueId: string, memberCount?: number): ReadinessChecklistItem {
  const href = `${leagueBase(leagueId)}/members`;
  const count = memberCount ?? 0;
  if (count >= 2) {
    return {
      id: 'members',
      label: 'Member roster exists',
      status: 'complete',
      explanation: `${count} members on roster.`,
      actionLabel: 'Manage members',
      actionHref: href,
    };
  }
  if (count === 1) {
    return {
      id: 'members',
      label: 'Member roster exists',
      status: 'warning',
      explanation: 'Add at least one more member so games can be matched.',
      actionLabel: 'Add members',
      actionHref: href,
    };
  }
  return {
    id: 'members',
    label: 'Member roster exists',
    status: 'blocked',
    explanation: 'Add league members before importing a schedule or syncing VODs.',
    actionLabel: 'Add members',
    actionHref: href,
  };
}

function buildTwitchItem(
  leagueId: string,
  memberCount?: number,
  vodSyncReadiness?: LscVodSyncReadiness | null,
  liveReadiness?: LscLiveReadinessReport | null
): ReadinessChecklistItem {
  const href = `${leagueBase(leagueId)}/members`;
  const missingFromLive = liveReadiness?.members_missing_twitch_count ?? 0;
  const totalMembers =
    vodSyncReadiness?.total_current_members ?? memberCount ?? liveReadiness?.eligible_live_game_members_count;
  const eligible = vodSyncReadiness?.eligible_count ?? 0;
  const missingTwitch =
    vodSyncReadiness?.reason_counts?.missing_twitch ??
    (totalMembers !== undefined ? Math.max(0, totalMembers - eligible) : missingFromLive);

  if ((memberCount ?? 0) === 0) {
    return {
      id: 'twitch',
      label: 'Twitch identities mapped',
      status: 'blocked',
      explanation: 'Add members first, then link Twitch accounts.',
      actionLabel: 'Manage members',
      actionHref: href,
    };
  }

  if (missingTwitch <= 0 && eligible >= 2) {
    return {
      id: 'twitch',
      label: 'Twitch identities mapped',
      status: 'complete',
      explanation: `${eligible} members are ready with Twitch identities mapped.`,
      actionLabel: 'Review members',
      actionHref: href,
    };
  }

  if (eligible > 0) {
    return {
      id: 'twitch',
      label: 'Twitch identities mapped',
      status: 'warning',
      explanation: `${missingTwitch || 'Some'} member(s) still need Twitch mapping before automation can run.`,
      actionLabel: 'Fix Twitch mappings',
      actionHref: href,
    };
  }

  return {
    id: 'twitch',
    label: 'Twitch identities mapped',
    status: 'blocked',
    explanation: 'Link Twitch accounts for league members.',
    actionLabel: 'Map Twitch identities',
    actionHref: href,
  };
}

function buildScheduleItem(
  leagueId: string,
  matchCount?: number,
  liveReadiness?: LscLiveReadinessReport | null
): ReadinessChecklistItem {
  const href = `${leagueBase(leagueId)}/import`;
  const matches = matchCount ?? 0;
  const stale = liveReadiness?.is_schedule_stale;

  if (matches > 0 && !stale) {
    return {
      id: 'schedule',
      label: 'Schedule imported or synced',
      status: 'complete',
      explanation: `${matches} game${matches === 1 ? '' : 's'} on the schedule.`,
      actionLabel: NAV_LABEL_IMPORT_SYNC,
      actionHref: href,
    };
  }

  if (matches > 0 && stale) {
    return {
      id: 'schedule',
      label: 'Schedule imported or synced',
      status: 'warning',
      explanation: 'Schedule exists but may be stale — refresh import or NeonSportz sync.',
      actionLabel: 'Refresh schedule',
      actionHref: href,
    };
  }

  return {
    id: 'schedule',
    label: 'Schedule imported or synced',
    status: 'blocked',
    explanation: 'Import a CSV or sync NeonSportz to load league games.',
    actionLabel: NAV_LABEL_IMPORT_SYNC,
    actionHref: href,
  };
}

function buildEventSubItem(
  leagueId: string,
  liveReadiness?: LscLiveReadinessReport | null
): ReadinessChecklistItem {
  const href = `${leagueBase(leagueId)}/settings`;
  const eventsub = liveReadiness?.eventsub_subscription_status;
  const domainBlocked = liveReadiness?.stream_domain_status?.status === 'blocked';

  if (!liveReadiness) {
    return {
      id: 'eventsub',
      label: 'Twitch Live Detection ready',
      status: 'warning',
      explanation: 'Live readiness has not loaded yet.',
      actionLabel: 'Open live detection',
      actionHref: href,
    };
  }

  if (domainBlocked) {
    return {
      id: 'eventsub',
      label: 'Twitch Live Detection ready',
      status: 'blocked',
      explanation: 'Live detection callback or stream domain is not configured.',
      actionLabel: 'Review live detection',
      actionHref: href,
    };
  }

  const liveState = eventsub?.live_detection_state;
  if (liveState === 'ready' || eventsub?.status === 'ready') {
    return {
      id: 'eventsub',
      label: 'Twitch Live Detection ready',
      status: 'complete',
      explanation: 'Live detection is automatic for connected members.',
      actionLabel: 'Review live detection',
      actionHref: href,
    };
  }

  const pending = eventsub?.members_pending_verification ?? 0;
  const missing =
    (eventsub?.members_missing_online ?? 0) + (eventsub?.members_missing_offline ?? 0);
  const failed = eventsub?.members_failed ?? 0;

  if (failed > 0 || liveState === 'failed') {
    return {
      id: 'eventsub',
      label: 'Twitch Live Detection ready',
      status: 'blocked',
      explanation: 'Twitch alerts need attention for one or more members.',
      actionLabel: 'Repair Twitch alerts',
      actionHref: href,
    };
  }

  if (pending > 0 || missing > 0 || liveState === 'provisioning' || eventsub?.status === 'warning') {
    return {
      id: 'eventsub',
      label: 'Twitch Live Detection ready',
      status: 'warning',
      explanation: 'Setting up Twitch alerts for connected members.',
      actionLabel: 'Review live detection',
      actionHref: href,
    };
  }

  return {
    id: 'eventsub',
    label: 'Twitch Live Detection ready',
    status: 'blocked',
    explanation: 'Twitch live/offline alerts are missing for one or more broadcasters.',
    actionLabel: 'Repair Twitch alerts',
    actionHref: href,
  };
}

function buildLiveTruthItem(
  leagueId: string,
  digest?: LscCommissionerDigest | null,
  billingStatus?: LscBillingStatusResponse | null
): ReadinessChecklistItem {
  const href = `${leagueBase(leagueId)}/settings`;
  if (!isAutomationCoverageEligible(digest)) {
    return {
      id: 'live_truth',
      label: 'Live game detection',
      status: 'warning',
      explanation: automationNotEnabledCopy(billingStatus, digest),
      actionLabel: 'View live readiness',
      actionHref: href,
    };
  }
  return {
    id: 'live_truth',
    label: 'Live game detection',
    status: 'complete',
    explanation: automationEnabledCopy(digest),
    actionLabel: 'View live detection status',
    actionHref: leagueBase(leagueId),
  };
}

function buildVodAutomationItem(
  leagueId: string,
  digest?: LscCommissionerDigest | null,
  billingStatus?: LscBillingStatusResponse | null
): ReadinessChecklistItem {
  const coverage = getAutomationCoverage(digest);
  const vodSyncCoverage = digest?.automation?.vod_sync_coverage;

  if (!isAutomationCoverageEligible(digest)) {
    return {
      id: 'vod_automation',
      label: 'Archive automation',
      status: 'warning',
      explanation: automationNotEnabledCopy(billingStatus, digest),
      actionLabel: 'Review archive automation settings',
      actionHref: `${leagueBase(leagueId)}/settings/archive-automation`,
    };
  }

  const prepPreviewOnly =
    coverage?.dry_run_only === true ||
    (coverage?.vod_handoff_apply_enabled !== true &&
      digest?.automation?.scheduled_apply_enabled !== true);
  const vodSyncPreviewOnly =
    vodSyncCoverage?.dry_run_only !== false ||
    vodSyncCoverage?.scheduled_apply_enabled !== true;

  if (prepPreviewOnly || vodSyncPreviewOnly) {
    const syncNote =
      vodSyncCoverage?.eligible === true
        ? ' Archive automation is running in preview mode. It can find recent replays for review, but it will not create or publish archive entries until automatic updates are approved.'
        : '';
    return {
      id: 'vod_automation',
      label: 'Archive automation',
      status: 'warning',
      explanation: `${automationEnabledCopy(digest)} ${VOD_PREP_PREVIEW_COPY}${syncNote}`,
      actionLabel: 'Review archive automation settings',
      actionHref: `${leagueBase(leagueId)}/settings/archive-automation`,
    };
  }

  return {
    id: 'vod_automation',
    label: 'Archive automation',
    status: 'complete',
    explanation: automationEnabledCopy(digest),
    actionLabel: 'Review archive automation settings',
    actionHref: `${leagueBase(leagueId)}/settings/archive-automation`,
  };
}

function buildReviewQueueItem(
  leagueId: string,
  digest?: LscCommissionerDigest | null,
  candidateCount?: number
): ReadinessChecklistItem {
  const href = `${leagueBase(leagueId)}/needs-attention`;
  const pending = digest?.counts?.pending_review ?? 0;
  const identity = digest?.counts?.needs_identity_attestation ?? 0;
  const needsAction = pending + identity;

  if (needsAction > 0) {
    return {
      id: 'review_queue',
      label: 'Needs Attention',
      status: 'warning',
      explanation: `${needsAction} game${needsAction === 1 ? '' : 's'} need commissioner review before archiving.`,
      actionLabel: 'Open Needs Attention',
      actionHref: href,
    };
  }

  if ((candidateCount ?? 0) > 0) {
    return {
      id: 'review_queue',
      label: 'Needs Attention',
      status: 'complete',
      explanation: 'Needs Attention is clear — no games waiting for review.',
      actionLabel: 'Open Needs Attention',
      actionHref: href,
    };
  }

  return {
    id: 'review_queue',
    label: 'Needs Attention',
    status: 'warning',
    explanation: 'Import your schedule and connect Twitch so live games can be matched for review.',
    actionLabel: 'Open setup wizard',
    actionHref: `${leagueBase(leagueId)}/setup`,
  };
}

function buildArchiveItem(leagueId: string, archiveCount?: number): ReadinessChecklistItem {
  const href = `${leagueBase(leagueId)}/archive`;
  const published = archiveCount ?? 0;
  const baseExplanation = `${ARCHIVE_COMMISSIONER_COPY} Nothing is published automatically.`;

  if (published > 0) {
    return {
      id: 'archive',
      label: 'Archive publish ready',
      status: 'complete',
      explanation: `${baseExplanation} ${published} game${published === 1 ? '' : 's'} published.`,
      actionLabel: 'View archive',
      actionHref: href,
    };
  }

  return {
    id: 'archive',
    label: 'Archive publish ready',
    status: 'warning',
    explanation: `${baseExplanation} No published archive games yet.`,
    actionLabel: 'Open archive',
    actionHref: href,
  };
}

function resolveNextAction(
  leagueId: string,
  input: PaidBetaReadinessInput
): PaidBetaNextAction | null {
  const base = leagueBase(leagueId);
  const billing = input.billingStatus;
  const settingsHref = `${base}/settings`;
  const billingHref = lscPath('/billing');

  if (billing?.billing_model_enabled && isBillingMissing(billing)) {
    return {
      priority: 1,
      title: 'Activate subscription',
      detail: 'Subscribe before league automation and archive tools can go live.',
      actionLabel: 'Subscribe or manage billing',
      actionHref: settingsHref,
      severity: 'blocked',
    };
  }

  if (billing?.billing_model_enabled && isSubscriptionUnbound(billing)) {
    return {
      priority: 1,
      title: 'Link your subscription to this league',
      detail:
        'Your subscription is active at the account level but not linked to this league yet.',
      actionLabel: 'Link in league settings',
      actionHref: settingsHref,
      severity: 'blocked',
    };
  }

  if (billing?.billing_model_enabled && !hasActiveLeagueEntitlement(billing)) {
    return {
      priority: 2,
      title: 'No active subscription for this league',
      detail: 'This league needs an active subscription before league automation can run.',
      actionLabel: 'Fix league access',
      actionHref: settingsHref,
      severity: 'blocked',
    };
  }

  const members = input.memberCount ?? 0;
  if (members < 2) {
    return {
      priority: 3,
      title: 'Build your member roster',
      detail: 'Add league members so schedules, Twitch mapping, and VOD review can work.',
      actionLabel: 'Add members',
      actionHref: `${base}/members`,
      severity: members === 0 ? 'blocked' : 'warning',
    };
  }

  const eligible = input.vodSyncReadiness?.eligible_count ?? 0;
  const missingTwitch = input.vodSyncReadiness?.reason_counts?.missing_twitch ?? 0;
  const liveMissing = input.liveReadiness?.members_missing_twitch_count ?? 0;
  if (eligible < 2 || missingTwitch > 0 || liveMissing > 0) {
    return {
      priority: 4,
      title: 'Map Twitch identities for members',
      detail: 'Link Twitch accounts so live detection and VOD sync can find the right broadcasters.',
      actionLabel: 'Fix Twitch mappings',
      actionHref: `${base}/members`,
      severity: 'warning',
    };
  }

  const matches = input.matchCount ?? 0;
  if (matches <= 0) {
    return {
      priority: 5,
      title: 'Import or sync your schedule',
      detail: 'Load league games via CSV import or NeonSportz sync.',
      actionLabel: NAV_LABEL_IMPORT_SYNC,
      actionHref: `${base}/import`,
      severity: 'blocked',
    };
  }

  const eventsubStatus = input.liveReadiness?.eventsub_subscription_status?.status;
  if (eventsubStatus && eventsubStatus !== 'ready') {
    return {
      priority: 6,
      title: 'Finish Twitch Live Detection setup',
      detail: 'Twitch alerts are missing or still setting up for one or more members.',
      actionLabel: 'Set up live detection',
      actionHref: settingsHref,
      severity: 'blocked',
    };
  }

  if (!isAutomationCoverageEligible(input.digest)) {
    return {
      priority: 7,
      title: 'Automation rollout pending for this league',
      detail: automationNotEnabledCopy(input.billingStatus, input.digest),
      actionLabel: 'View readiness checklist',
      actionHref: base,
      severity: 'info',
    };
  }

  const reviewPending =
    (input.digest?.counts?.pending_review ?? 0) +
    (input.digest?.counts?.needs_identity_attestation ?? 0);
  if (reviewPending > 0) {
    return {
      priority: 8,
      title: 'Needs Attention',
      detail: `${reviewPending} game${reviewPending === 1 ? '' : 's'} waiting for commissioner review.`,
      actionLabel: 'Open Needs Attention',
      actionHref: `${base}/needs-attention`,
      severity: 'warning',
    };
  }

  const published = input.archiveCount ?? 0;
  if (published <= 0) {
    return {
      priority: 9,
      title: 'Publish your first archive game',
      detail: `${ARCHIVE_COMMISSIONER_COPY} Review matched VODs and publish when ready.`,
      actionLabel: 'Open archive',
      actionHref: `${base}/archive`,
      severity: 'info',
    };
  }

  return {
    priority: 10,
    title: 'League setup complete',
    detail: 'Core setup is in place. Use the tools below to monitor live games, VODs, and archive.',
    actionLabel: 'Manage billing',
    actionHref: billingHref,
    severity: 'success',
  };
}

export function buildPaidBetaReadinessAssessment(
  input: PaidBetaReadinessInput
): PaidBetaReadinessAssessment {
  const { leagueId } = input;
  const items: ReadinessChecklistItem[] = [
    buildBillingItem(leagueId, input.billingStatus, input.isOwner),
    buildMembersItem(leagueId, input.memberCount),
    buildTwitchItem(leagueId, input.memberCount, input.vodSyncReadiness, input.liveReadiness),
    buildScheduleItem(leagueId, input.matchCount, input.liveReadiness),
    buildEventSubItem(leagueId, input.liveReadiness),
    buildLiveTruthItem(leagueId, input.digest, input.billingStatus),
    buildVodAutomationItem(leagueId, input.digest, input.billingStatus),
    buildReviewQueueItem(leagueId, input.digest, input.candidateCount),
    buildArchiveItem(leagueId, input.archiveCount),
  ];

  const nextAction = resolveNextAction(leagueId, input);
  const setupComplete = nextAction?.priority === 10;
  const actionableIncomplete =
    nextAction !== null && nextAction.priority < 10 && nextAction.severity !== 'info';

  const completedCount = items.filter((item) => item.status === 'complete').length;

  return {
    items,
    nextAction,
    setupComplete,
    setupIncomplete: !setupComplete && (actionableIncomplete || nextAction?.severity === 'info'),
    completedCount,
  };
}

export function shouldExpandCommissionerCockpit(assessment: PaidBetaReadinessAssessment): boolean {
  if (assessment.setupComplete) return false;
  if (assessment.shouldShowChecklist === false) return false;
  const priority = assessment.nextAction?.priority ?? 10;
  return priority < 10;
}

export function mapLeagueReadinessToAssessment(readiness: LscLeagueReadiness): PaidBetaReadinessAssessment {
  return mapReadinessToCommandCenterChecklist(readiness);
}

export function leagueReadinessSummaryLabel(
  overallStatus: LscLeagueReadinessOverallStatus | undefined
): string {
  switch (overallStatus) {
    case 'ready':
      return 'League ready';
    case 'blocked':
      return 'Blocked';
    case 'warning':
      return 'Almost ready';
    case 'preview_only':
      return 'Preview only';
    default:
      return 'Needs attention';
  }
}
